import type { Attribute } from '@strapi/strapi';
import type { Got } from 'got/dist/source';
import type { KyInstance } from 'ky/distribution/types/ky';
import * as Sentry from '@sentry/nextjs';
import qs from 'qs';

import type { Locale, LocalePrefix } from '@tmh-website/shared/const';
import type { ResponseCollectionType } from '@tmh-website/shared/cms';
import { DefaultLocalePrefix } from '@tmh-website/shared/const';
import { isLocalePrefix } from '@tmh-website/services/utils.service';
import { PrefixToLocaleMap } from '@tmh-website/shared/const';

export type AudienceEntry = Attribute.GetValues<'api::audience.audience'>;

/**
 * Resolves the list of all the audiences defined in the CMS.
 *
 * @throws Error - When failed to fetch the audiences.
 */
export async function universalGetAudiences(
  fetcherInstance: Got | KyInstance,
  localePrefix: LocalePrefix | 'default'
): Promise<ResponseCollectionType<AudienceEntry>> {
  const locale: Locale = isLocalePrefix(localePrefix)
    ? PrefixToLocaleMap[localePrefix]
    : PrefixToLocaleMap[DefaultLocalePrefix];

  let response: ResponseCollectionType<AudienceEntry>;

  try {
    response = await fetcherInstance
      .get('audiences', { searchParams: qs.stringify({ locale }) })
      .json<ResponseCollectionType<AudienceEntry>>();
  } catch (caughtError) {
    const error = new Error(`Failed to fetch the audiences!`);
    error.stack = String(caughtError);

    if (process.env.NODE_ENV !== 'development') {
      Sentry.captureException(error);
    }

    throw error;
  }

  return response;
}
